import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  SignalIcon, 
  CheckCircleIcon, 
  ExclamationTriangleIcon,
  XCircleIcon,
  ClockIcon,
  CalendarIcon,
  ArrowUpIcon,
  ArrowDownIcon
} from '@heroicons/react/24/outline';
import AppHeader from '../components/AppHeader';
import AppFooter from '../components/AppFooter';

const StatusPage: React.FC = () => {
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Mock status data - in a real app, this would come from an API
  const systemStatus = {
    overall: 'operational',
    uptime: '99.98%',
    lastIncident: '2024-12-10',
    responseTime: '45ms'
  };

  const services = [
    {
      name: 'Web Application',
      status: 'operational',
      uptime: '99.99%',
      responseTime: '45ms',
      lastCheck: '2 minutes ago'
    },
    {
      name: 'API Services',
      status: 'operational',
      uptime: '99.97%',
      responseTime: '120ms',
      lastCheck: '1 minute ago'
    },
    {
      name: 'Database',
      status: 'operational',
      uptime: '99.99%',
      responseTime: '15ms',
      lastCheck: '30 seconds ago'
    },
    {
      name: 'File Storage',
      status: 'operational',
      uptime: '99.95%',
      responseTime: '200ms',
      lastCheck: '1 minute ago'
    },
    {
      name: 'Real-time Collaboration',
      status: 'operational',
      uptime: '99.98%',
      responseTime: '80ms',
      lastCheck: '45 seconds ago'
    }
  ];

  const incidents = [
    {
      id: 1,
      title: 'Scheduled Maintenance - Database Optimization',
      status: 'resolved',
      severity: 'maintenance',
      startTime: '2024-12-10T02:00:00Z',
      endTime: '2024-12-10T04:00:00Z',
      description: 'Database optimization and performance improvements completed successfully.',
      updates: [
        {
          time: '2024-12-10T04:00:00Z',
          message: 'Maintenance completed. All services are operational.'
        },
        {
          time: '2024-12-10T02:30:00Z',
          message: 'Database optimization in progress. Expected completion in 1.5 hours.'
        },
        {
          time: '2024-12-10T02:00:00Z',
          message: 'Scheduled maintenance started. Some features may be temporarily unavailable.'
        }
      ]
    },
    {
      id: 2,
      title: 'API Response Time Degradation',
      status: 'resolved',
      severity: 'minor',
      startTime: '2024-12-08T14:30:00Z',
      endTime: '2024-12-08T16:45:00Z',
      description: 'Increased API response times were detected and resolved.',
      updates: [
        {
          time: '2024-12-08T16:45:00Z',
          message: 'Issue resolved. API response times are back to normal.'
        },
        {
          time: '2024-12-08T15:30:00Z',
          message: 'Investigating increased API response times.'
        }
      ]
    }
  ];

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'operational':
        return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
      case 'degraded':
        return <ExclamationTriangleIcon className="w-5 h-5 text-yellow-500" />;
      case 'outage':
        return <XCircleIcon className="w-5 h-5 text-red-500" />;
      case 'maintenance':
        return <ClockIcon className="w-5 h-5 text-blue-500" />;
      default:
        return <SignalIcon className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'operational':
        return 'bg-green-100 text-green-800';
      case 'degraded':
        return 'bg-yellow-100 text-yellow-800';
      case 'outage':
        return 'bg-red-100 text-red-800';
      case 'maintenance':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center">
              <SignalIcon className="w-8 h-8 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">System Status</h1>
          <p className="text-xl text-gray-600 mb-4">
            Real-time status of StyleSync services and infrastructure.
          </p>
          <div className="flex items-center justify-center text-sm text-gray-500">
            <ClockIcon className="w-4 h-4 mr-2" />
            Last updated: {currentTime.toLocaleString()}
          </div>
        </motion.div>

        {/* Overall Status */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 mb-8"
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-semibold text-gray-900">Overall System Status</h2>
            <div className="flex items-center space-x-2">
              {getStatusIcon(systemStatus.overall)}
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(systemStatus.overall)}`}>
                {systemStatus.overall.charAt(0).toUpperCase() + systemStatus.overall.slice(1)}
              </span>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">{systemStatus.uptime}</div>
              <div className="text-sm text-gray-600">Uptime (30 days)</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">{systemStatus.responseTime}</div>
              <div className="text-sm text-gray-600">Avg Response Time</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">0</div>
              <div className="text-sm text-gray-600">Active Incidents</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">5</div>
              <div className="text-sm text-gray-600">Services Monitored</div>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Services Status */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">Service Status</h2>
            <div className="space-y-4">
              {services.map((service, index) => (
                <div key={index} className="bg-white rounded-lg border border-gray-200 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      {getStatusIcon(service.status)}
                      <h3 className="font-semibold text-gray-900">{service.name}</h3>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(service.status)}`}>
                      {service.status.charAt(0).toUpperCase() + service.status.slice(1)}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <div className="text-gray-600">Uptime</div>
                      <div className="font-medium text-gray-900">{service.uptime}</div>
                    </div>
                    <div>
                      <div className="text-gray-600">Response Time</div>
                      <div className="font-medium text-gray-900">{service.responseTime}</div>
                    </div>
                    <div>
                      <div className="text-gray-600">Last Check</div>
                      <div className="font-medium text-gray-900">{service.lastCheck}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Recent Incidents */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">Recent Incidents</h2>
            <div className="space-y-4">
              {incidents.map((incident) => (
                <div key={incident.id} className="bg-white rounded-lg border border-gray-200 p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 mb-2">{incident.title}</h3>
                      <p className="text-sm text-gray-600 mb-3">{incident.description}</p>
                      <div className="flex items-center space-x-4 text-xs text-gray-500">
                        <span>Started: {formatDate(incident.startTime)}</span>
                        <span>Resolved: {formatDate(incident.endTime)}</span>
                      </div>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(incident.status)}`}>
                      {incident.status.charAt(0).toUpperCase() + incident.status.slice(1)}
                    </span>
                  </div>
                  
                  <div className="border-t border-gray-100 pt-4">
                    <h4 className="text-sm font-medium text-gray-900 mb-3">Updates</h4>
                    <div className="space-y-2">
                      {incident.updates.map((update, index) => (
                        <div key={index} className="flex items-start space-x-3">
                          <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                          <div className="flex-1">
                            <div className="text-xs text-gray-500">{formatDate(update.time)}</div>
                            <div className="text-sm text-gray-700">{update.message}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Subscribe to Updates */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mt-12 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl border border-blue-200 p-8"
        >
          <div className="text-center">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Stay Updated</h2>
            <p className="text-gray-600 mb-6">
              Get notified about system status updates and incidents via email.
            </p>
            <div className="max-w-md mx-auto">
              <div className="flex space-x-3">
                <input
                  type="email"
                  placeholder="Enter your email address"
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-200">
                  Subscribe
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </main>

      <AppFooter />
    </div>
  );
};

export default StatusPage; 